import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Eye, Globe, Smartphone, Monitor, Loader2 } from 'lucide-react';
import { Card, CardTitle } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme';
import { useLinkPageStats } from '@/hooks/useLinkPageStats';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';

interface LinkPageStatsProps {
  linkpageId: string;
}

const periodOptions: { value: Period; labelKey: string }[] = [
  { value: 'week', labelKey: 'linkPages.stats.week' },
  { value: 'month', labelKey: 'linkPages.stats.month' },
  { value: 'year', labelKey: 'linkPages.stats.year' },
  { value: 'all', labelKey: 'linkPages.stats.allTime' },
];

const LinkPageStats = ({ linkpageId }: LinkPageStatsProps) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [period, setPeriod] = useState<Period>('month');
  const { stats, loading } = useLinkPageStats(linkpageId, period);

  if (loading) {
    return (
      <Card variant="default" padding="lg">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-lime-500" />
        </div>
      </Card>
    );
  }

  if (!stats) return null;

  const mobileViews = stats.viewsByDevice.find(d => d.device === 'mobile')?.views || 0;
  const totalViews = stats.totalViews || 0;
  const mobilePercentage = totalViews > 0 ? Math.round((mobileViews / totalViews) * 100) : 0;
  const countries = stats.viewsByCountry.length;

  // Chart data
  const chartCategories = stats.viewsByDay.map((item) =>
    new Date(item.date).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    })
  );

  const chartSeries = [
    {
      name: t('linkPages.stats.views'),
      data: stats.viewsByDay.map((item) => item.views),
    },
  ];

  const chartOptions: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    theme: { mode: isDark ? 'dark' : 'light' },
    stroke: { curve: 'smooth', width: 3, colors: ['#D4E157'] },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color: '#D4E157', opacity: 0.3 },
          { offset: 100, color: '#D4E157', opacity: 0 },
        ],
      },
    },
    colors: ['#D4E157'],
    xaxis: {
      categories: chartCategories,
      labels: { style: { colors: isDark ? '#9CA3AF' : '#6B7280', fontSize: '12px' } },
      axisBorder: { color: isDark ? '#374151' : '#E5E7EB' },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: isDark ? '#9CA3AF' : '#6B7280', fontSize: '12px' } },
    },
    grid: { borderColor: isDark ? '#374151' : '#f0f0f0', strokeDashArray: 3 },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: { fontSize: '14px' },
      y: { formatter: (value: number) => `${value} ${t('linkPages.stats.views').toLowerCase()}` },
    },
    dataLabels: { enabled: false },
  };

  const kpis = [
    {
      label: t('linkPages.stats.totalViews'),
      value: totalViews.toLocaleString(i18n.language),
      icon: <Eye className="w-5 h-5" />,
      color: 'bg-lime-100 text-lime-600 dark:bg-lime-900/30 dark:text-lime-400',
    },
    {
      label: t('linkPages.stats.countries'),
      value: countries.toString(),
      icon: <Globe className="w-5 h-5" />,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    {
      label: t('linkPages.stats.mobile'),
      value: `${mobilePercentage}%`,
      icon: <Smartphone className="w-5 h-5" />,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      label: t('linkPages.stats.desktop'),
      value: `${totalViews > 0 ? 100 - mobilePercentage : 0}%`,
      icon: <Monitor className="w-5 h-5" />,
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header with period selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-dark-800 dark:text-white">
          {t('linkPages.stats.title')}
        </h3>
        <div className="flex gap-1 bg-gray-100 dark:bg-dark-700 rounded-lg p-1">
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                period === opt.value
                  ? 'bg-white dark:bg-dark-600 text-dark-800 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((kpi, index) => (
          <Card key={index} variant="default" padding="md">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${kpi.color}`}>
                {kpi.icon}
              </div>
              <div>
                <p className="text-lg font-bold text-dark-800 dark:text-white">{kpi.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{kpi.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Views chart */}
      {stats.viewsByDay.length > 0 && (
        <Card variant="default" padding="lg">
          <CardTitle className="mb-4">{t('linkPages.stats.viewsOverTime')}</CardTitle>
          <div className="h-[250px]">
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="area"
              height="100%"
              width="100%"
            />
          </div>
        </Card>
      )}

      {/* Top countries */}
      {stats.viewsByCountry.length > 0 && (
        <Card variant="default" padding="lg">
          <CardTitle className="mb-4">{t('linkPages.stats.topCountries')}</CardTitle>
          <div className="space-y-2">
            {stats.viewsByCountry.slice(0, 5).map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">{item.country}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-100 dark:bg-dark-700 rounded-full h-2">
                    <div
                      className="bg-lime-500 h-2 rounded-full"
                      style={{ width: `${totalViews > 0 ? (item.views / totalViews) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-dark-800 dark:text-white w-10 text-right">
                    {item.views}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default LinkPageStats;
